/**
 * Smart Date Formatting Utilities
 *
 * Provides context-aware date formatting similar to Cloze CRM:
 * - Today: "8am", "2pm"
 * - Yesterday: "Yesterday"
 * - This week: "Mon", "Tue"
 * - Recent: "Jan 15"
 * - Older: "5 months ago", "1 year ago"
 */
/**
 * Format a date intelligently based on how recent it is
 * @param date - The date to format
 * @returns A human-friendly date string
 */
export function formatSmartDate(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    // Reset time portions for day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    const diffMs = today.getTime() - compareDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    // Today: show time
    if (diffDays === 0) {
        return formatTimeOnly(dateObj);
    }
    // Yesterday
    if (diffDays === 1) {
        return 'Yesterday';
    }
    // Tomorrow (negative diff)
    if (diffDays === -1) {
        return 'Tomorrow';
    }
    // This week (within 7 days)
    if (diffDays > 0 && diffDays < 7) {
        return dateObj.toLocaleDateString('en-US', { weekday: 'short' });
    }
    // Next week (within next 7 days)
    if (diffDays < 0 && diffDays > -7) {
        return dateObj.toLocaleDateString('en-US', { weekday: 'short' });
    }
    // This month or next month
    if (Math.abs(diffDays) < 30) {
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    // Older: use relative time
    return formatRelativeTime(dateObj);
}
/**
 * Format time only (for today's dates)
 * @param date - The date to format
 * @returns Time string like "8am" or "2pm"
 */
export function formatTimeOnly(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    // Only show minutes if not on the hour
    if (minutes === 0) {
        return `${displayHours}${ampm}`;
    }
    return `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}`;
}
/**
 * Format date as relative time for older dates
 * @param date - The date to format
 * @returns Relative time string like "5 months ago" or "1 year ago"
 */
export function formatRelativeTime(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);
    // Future dates
    if (diffMs < 0) {
        const futureDays = Math.abs(diffDays);
        const futureWeeks = Math.abs(diffWeeks);
        const futureMonths = Math.abs(diffMonths);
        if (futureDays < 7)
            return `in ${futureDays} ${futureDays === 1 ? 'day' : 'days'}`;
        if (futureWeeks < 4)
            return `in ${futureWeeks} ${futureWeeks === 1 ? 'week' : 'weeks'}`;
        if (futureMonths < 12)
            return `in ${futureMonths} ${futureMonths === 1 ? 'month' : 'months'}`;
        const futureYears = Math.abs(diffYears);
        return `in ${futureYears} ${futureYears === 1 ? 'year' : 'years'}`;
    }
    // Past dates
    if (diffDays < 7)
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    if (diffWeeks < 4)
        return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    if (diffMonths < 12)
        return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
}
/**
 * Format context-aware date with optional override
 * @param date - The date to format
 * @param context - Force specific format: 'time', 'date', or 'relative'
 * @returns Formatted date string
 */
export function formatContextualDate(date, context) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    switch (context) {
        case 'time':
            return formatTimeOnly(dateObj);
        case 'date':
            return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        case 'relative':
            return formatRelativeTime(dateObj);
        default:
            return formatSmartDate(dateObj);
    }
}
/**
 * Format communication gap message
 * @param lastContactDate - The date of last contact (optional)
 * @param activityType - Type of last activity (email, call, meeting, etc.)
 * @returns Context message like "Last mail 5 months ago" or "No recent contact"
 */
export function formatCommunicationGap(lastContactDate, activityType) {
    if (!lastContactDate) {
        return 'No recent contact';
    }
    const dateObj = typeof lastContactDate === 'string' ? new Date(lastContactDate) : lastContactDate;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    // Format activity type
    const activityLabel = formatActivityType(activityType);
    // Today
    if (diffDays === 0) {
        return `${activityLabel} today`;
    }
    // Yesterday
    if (diffDays === 1) {
        return `${activityLabel} yesterday`;
    }
    // This week
    if (diffDays < 7) {
        return `${activityLabel} ${diffDays} days ago`;
    }
    // Weeks
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) {
        return `${activityLabel} ${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
    }
    // Months
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
        return `${activityLabel} ${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    }
    // Years
    const diffYears = Math.floor(diffDays / 365);
    return `${activityLabel} ${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
}
/**
 * Format activity type for display
 * @param activityType - The activity type from database
 * @returns Human-friendly activity label
 */
function formatActivityType(activityType) {
    if (!activityType)
        return 'Last contact';
    const typeMap = {
        'email': 'Last mail',
        'call': 'Last call',
        'meeting': 'Last meeting',
        'note': 'Last note',
        'sms': 'Last message',
        'linkedin': 'Last LinkedIn message'
    };
    return typeMap[activityType.toLowerCase()] || 'Last contact';
}
/**
 * Get days since date
 * @param date - The date to compare
 * @returns Number of days since the date
 */
export function getDaysSince(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}
/**
 * Format date label for navigation
 * @param date - The date to format
 * @returns Label like "Today", "Yesterday", "Jan 15", etc.
 */
export function formatDateLabel(date) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffMs = today.getTime() - compareDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0)
        return 'Today';
    if (diffDays === 1)
        return 'Yesterday';
    if (diffDays === -1)
        return 'Tomorrow';
    // This year: show month and day
    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    // Other years: include year
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
